import { useState, useEffect } from 'react';
import { ACM } from '../../constants/UserRoles';

export const useNotificationState = (notifications, typeOfUser) => {
  const [notificationsList, setNotificationsList] = useState(notifications);
  const [filteredNotifications, setFilteredNotifications] = useState(notifications);
  const [activeFilter, setActiveFilter] = useState('all');

  // Filter notifications based on active filter
  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredNotifications(notificationsList);
    } else {
      const filtered = notificationsList.filter((notification) => {
        switch (activeFilter) {
          case 'messages':
            return [
              'patientMessage',
              'doctorMessage',
              'teacherMessage',
              'studentMessage',
              'groupMessage',
            ].includes(notification.notificationSource);
          case 'videos':
            return [
              'patientVideoUploaded',
              'doctorVideoUploaded',
              'teacherVideoUploaded',
              'studentVideoUploaded',
              'uploadVideo', // Generic video upload source
            ].includes(notification.notificationSource);
          case 'attachments':
            return [
              'patientDocumentUploaded',
              'doctorDocumentUploaded',
              'teacherDocumentUploaded',
              'studentDocumentUploaded',
              'patientAttachmentUploaded',
              'doctorAttachmentUploaded',
              'teacherAttachmentUploaded',
              'studentAttachmentUploaded',
            ].includes(notification.notificationSource);
          case 'goals':
            return [
              'patientGoalCreated',
              'doctorGoalCreated',
              'teacherGoalCreated',
              'studentGoalCreated',
            ].includes(notification.notificationSource);
          case 'quicktracks':
            return [
              'patientBehaviorCreated',
              'doctorBehaviorCreated',
              'teacherBehaviorCreated',
              'studentBehaviorCreated',
            ].includes(notification.notificationSource);
          case 'profiles':
            return ['newPatientReg'].includes(notification.notificationSource);
          default:
            return true;
        }
      });
      setFilteredNotifications(filtered);
    }
  }, [notificationsList, activeFilter]);

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setActiveFilter(newFilter);
    }
  };

  // Process notifications based on user type and permissions
  useEffect(() => {
    if (!typeOfUser || !ACM[typeOfUser]) {
      console.error('Invalid typeOfUser or ACM configuration:', typeOfUser);
      setNotificationsList([]);
      return;
    }

    // Check if this user type can view notifications
    if (ACM[typeOfUser]?.canViewNotifications === false) {
      setNotificationsList([]);
      return;
    }

    if (!notifications || !Array.isArray(notifications)) {
      console.error('Notifications is empty or not an array');
      setNotificationsList([]);
      return;
    }

    let tempNotifications = [...notifications];

    if (ACM[typeOfUser]?.notificationServices || ACM[typeOfUser]?.isAdmin || typeOfUser === 'SUPER_ADMIN' || typeOfUser === 'CUSTOM_ROLE') {
      tempNotifications = tempNotifications
        .filter((notification) => {
          const isValidSource = [
            'newPatientReg',
            // All video upload types - should be visible to all users
            'patientVideoUploaded',
            'doctorVideoUploaded',
            'teacherVideoUploaded',
            'studentVideoUploaded',
            'uploadVideo', // Main video upload source
            // All attachment upload types (actual sources used)
            'patientDocumentUploaded',
            'doctorDocumentUploaded', 
            'teacherDocumentUploaded',
            'studentDocumentUploaded',
            // Legacy attachment sources (keep for backward compatibility)
            'patientAttachmentUploaded',
            'doctorAttachmentUploaded',
            'teacherAttachmentUploaded',
            'studentAttachmentUploaded',
            // All message types
            'teacherMessage',
            'doctorMessage',
            'patientMessage',
            'studentMessage',
            'groupMessage',
            // All goal types
            'teacherGoalCreated',
            'doctorGoalCreated',
            'studentGoalCreated',
            'patientGoalCreated',
            // All behavior types
            'teacherBehaviorCreated',
            'doctorBehaviorCreated',
            'studentBehaviorCreated',
            'patientBehaviorCreated',
            // Admin specific notifications
            'adminNotification',
            'systemNotification',
            'schoolAdminNotification',
            'organizationAdminNotification',
          ].includes(notification.notificationSource);
          const isUnread = notification?.readStatus === false;
          return isValidSource && isUnread;
        })
        .sort((a, b) => {
          const dateA = new Date(
            (a.createdDate?._seconds || a.createdDate?.seconds) * 1000 +
              (a.createdDate?._nanoseconds || a.createdDate?.nanoseconds) /
                1000000
          );
          const dateB = new Date(
            (b.createdDate?._seconds || b.createdDate?.seconds) * 1000 +
              (b.createdDate?._nanoseconds || b.createdDate?.nanoseconds) /
                1000000
          );
          return dateB - dateA;
        });
    } else {
      // Handle PATIENT, STUDENT, and other user types that can view notifications
      tempNotifications = tempNotifications
        .filter((notification) => {
          const isValidSource = [
            // All video upload types - should be visible to all users
            'patientVideoUploaded',
            'doctorVideoUploaded',
            'teacherVideoUploaded',
            'studentVideoUploaded',
            'uploadVideo', // Generic video upload source
            // All attachment upload types (actual sources used)
            'patientDocumentUploaded',
            'doctorDocumentUploaded', 
            'teacherDocumentUploaded',
            'studentDocumentUploaded',
            // Legacy attachment sources (keep for backward compatibility)
            'patientAttachmentUploaded',
            'doctorAttachmentUploaded',
            'teacherAttachmentUploaded',
            'studentAttachmentUploaded',
            // All message types
            'patientMessage',
            'doctorMessage',
            'teacherMessage',
            'studentMessage',
            'groupMessage',
            // All goal types
            'patientGoalCreated',
            'doctorGoalCreated',
            'teacherGoalCreated',
            'studentGoalCreated',
            // All behavior types
            'patientBehaviorCreated',
            'doctorBehaviorCreated',
            'teacherBehaviorCreated',
            'studentBehaviorCreated',
            // General notifications that all users can see
            'systemNotification',
          ].includes(notification.notificationSource);
          const isUnread = notification?.readStatus === false;
          return isValidSource && isUnread;
        })
        .sort((a, b) => {
          const dateA = new Date(
            (a.createdDate?._seconds || a.createdDate?.seconds) * 1000 +
              (a.createdDate?._nanoseconds || a.createdDate?.nanoseconds) /
                1000000
          );
          const dateB = new Date(
            (b.createdDate?._seconds || b.createdDate?.seconds) * 1000 +
              (b.createdDate?._nanoseconds || b.createdDate?.nanoseconds) /
                1000000
          );
          return dateB - dateA;
        });
    }

    setNotificationsList(tempNotifications);
  }, [notifications, typeOfUser]);

  return {
    notificationsList,
    filteredNotifications,
    activeFilter,
    handleFilterChange,
  };
}; 